import { Vector3 } from '@/math'
import type { Vec3 } from '../voxelTypes'
import { cellCenter } from './grid'

function abs(v: number) { return Math.abs(v) }

// Return the dominant axis ('x'|'y'|'z') of a vector by largest absolute component.
// Tie-breaks prefer Y, then X, then Z to match existing behavior in controller.
export function dominantAxisFromVec(v: Vec3 | Vector3): 'x' | 'y' | 'z' {
  const x = (v as any).x, y = (v as any).y, z = (v as any).z
  const ax = abs(x), ay = abs(y), az = abs(z)
  if (ay >= ax && ay >= az) return 'y'
  if (ax >= az) return 'x'
  return 'z'
}

// Choose the axis that best aligns from an anchor voxel center to a world-space point.
export function axisFromAnchorToPoint(
  anchor: { x: number; y: number; z: number },
  point: { x: number; y: number; z: number }
): 'x' | 'y' | 'z' {
  const c = cellCenter(anchor)
  const O = new Vector3(c.x, c.y, c.z)
  const P = new Vector3(point.x, point.y, point.z)
  const dyz2 = (P.y - O.y) ** 2 + (P.z - O.z) ** 2
  const dxz2 = (P.x - O.x) ** 2 + (P.z - O.z) ** 2
  const dxy2 = (P.x - O.x) ** 2 + (P.y - O.y) ** 2
  let axis: 'x' | 'y' | 'z' = 'x'
  let min = dyz2
  if (dxz2 < min) { axis = 'y'; min = dxz2 }
  if (dxy2 < min) { axis = 'z' }
  return axis
}

// C36: Normalize any vectorish normal to a single-axis integer unit {dx,dy,dz}.
// Chooses the dominant axis if the input isn't perfectly axis-aligned.
export function consistentNormalUnit(v: Vec3 | Vector3): { dx: number; dy: number; dz: number } {
  const x = (v as any).x, y = (v as any).y, z = (v as any).z
  const ax = abs(x), ay = abs(y), az = abs(z)
  if (ay >= ax && ay >= az) return { dx: 0, dy: Math.sign(y) || 1, dz: 0 }
  if (ax >= az) return { dx: Math.sign(x) || 1, dy: 0, dz: 0 }
  return { dx: 0, dy: 0, dz: Math.sign(z) || 1 }
}

// C46: Convert a normal to axis label; prefers exact axis when aligned, else dominant axis.
export function normalToAxis(v: Vec3 | Vector3): 'x' | 'y' | 'z' {
  const x = (v as any).x, y = (v as any).y, z = (v as any).z
  const ex = Math.abs(Math.abs(x) - 1) < 1e-6 && Math.abs(y) < 1e-6 && Math.abs(z) < 1e-6
  const ey = Math.abs(Math.abs(y) - 1) < 1e-6 && Math.abs(x) < 1e-6 && Math.abs(z) < 1e-6
  const ez = Math.abs(Math.abs(z) - 1) < 1e-6 && Math.abs(x) < 1e-6 && Math.abs(y) < 1e-6
  if (ex) return 'x'
  if (ey) return 'y'
  if (ez) return 'z'
  return dominantAxisFromVec({ x, y, z } as any)
}
